import React from 'react';
import T from 'prop-types';
// components
import { FormattedMessage, useIntl } from 'react-intl';
import ActivityActionsBar from './ActivityActionsBar/ActivityActionsBar';
import ActivityCheckupBox from './ActivityCheckupBox/ActivityCheckupBox';
import ActivityBox from './ActivityBox';
import ActivityBodyCollapse from './ActivityBodyCollapse';
import ActivityHeader from './ActivityHeader';
import ActivityAuthorLine from './ActivityAuthorLine/ActivityAuthorLine';
import ActivityMentions from './ActivityMentions';
import CollapsesBox from 'components/CollapsesBox/CollapsesBox';
import Collapse from 'components/Collapse/Collapse';
import FarmFeedRowComments from 'components/FarmFeed/FarmFeedRowComments';
import FarmFeedRowAddComment from 'components/FarmFeed/FarmFeedRowAddComment';
import LastCommentInfo from './LastCommentInfo/LastCommentInfo';
import Link from 'react-router/lib/Link';
// utils
import classnames from 'classnames/bind';
import moment from 'moment';
import { convertLbsToKg, convertTonsToLbs } from 'utils/weightHelper';
import { formatNumber } from 'utils';
// regional date handling
import { connect } from 'react-redux';
import { getCurrentCountryCode } from 'selectors/regionalDateSelectors';
import { formatDateForUser } from 'utils/regionalDateHelper';
// hoc
import withActivityController from './hoc/withActivityController';
// styles
import styles from './FeedOrderActivity.module.scss';

const cn = classnames.bind(styles);

const affectRerenderKeys = ['id', 'updated_at', 'is_viewed', 'flagged', 'comments'];

const FeedOrderActivity = ({
  className,
  activity,
  currentUser,
  isChildActivity,
  markAsSeen,
  openDropdown,
  setActivityFlag,
  isMinimized,
  isTruncated,
  toggleMinimized,
  onViewFullActivity,
  defaultDropdownOptions,
  countryCode,
}) => {
  const intl = useIntl();
  const {
    id,
    pig_group,
    farm,
    user,
    created_at,
    flagged,
    is_viewed,
    mentions,
    message,
    additional_data = {}
  } = activity;

  const isAdmin = currentUser.roles_map.admin;

  const {
    total_tons = 'N/A',
    total_pounds = undefined,
    delivery_date = 'TBD',
    farm_name = farm?.name || '',
    group_name = pig_group?.name || '',
    bins_data = []
  } = additional_data || {};

  const parsedData = React.useMemo(() => {
    if (additional_data && (additional_data.total_tons !== undefined || additional_data.total_pounds !== undefined)) {
      let totalPoundsValue;
      if (typeof total_pounds === 'number') {
        totalPoundsValue = total_pounds;
      } else if (typeof total_tons === 'number') {
        totalPoundsValue = convertTonsToLbs(total_tons);
      } else {
        totalPoundsValue = undefined;
      }
      const bins = Array.isArray(bins_data) ? bins_data : [];
      return {
        totalPounds: totalPoundsValue,
        deliveryDate: delivery_date,
        farmName: farm_name,
        groupName: group_name,
        binsData: bins.map((b) => {
          let quantityPounds;
          if (typeof b.quantity_pounds === 'number') {
            quantityPounds = b.quantity_pounds;
          } else if (typeof b.quantity === 'number') {
            quantityPounds = convertTonsToLbs(b.quantity);
          } else {
            quantityPounds = undefined;
          }
          return {
            name: b.name,
            quantityPounds
          };
        })
      };
    }

    if (message) {
      const lines = message.split('\n');
      const farmLine = lines.find((line) => line.includes('• Farm:'));
      const groupLine = lines.find((line) => line.includes('• Group:'));
      const totalLine = lines.find((line) => line.includes('• Total Tons Ordered:'));
      const deliveryLine = lines.find((line) => line.includes('• Delivery Date:'));

      const farmName = farmLine ? farmLine.replace('• Farm:', '').trim() : farm?.name || '';
      const groupName = groupLine ? groupLine.replace('• Group:', '').trim() : pig_group?.name || '';
      const totalTons = totalLine ? totalLine.replace('• Total Tons Ordered:', '').trim() : 'N/A';
      const deliveryDate = deliveryLine ? deliveryLine.replace('• Delivery Date:', '').trim() : 'TBD';

      const binBreakdownStart = lines.findIndex((line) => line.includes('• Bin Breakdown:'));
      const binItems = binBreakdownStart !== -1 ?
        lines.slice(binBreakdownStart + 1).filter((line) => line.trim().startsWith('–')) : [];

      const binsData = binItems.map((item, index) => {
        const cleanItem = item.replace('–', '').trim();
        const [name, quantity] = cleanItem.split(':').map((s) => s.trim());
        return { name: name || `Bin ${index + 1}`, quantityPounds: quantity || 'N/A' };
      });

      return {
        totalPounds: (totalTons && !Number.isNaN(Number(totalTons))) ? convertTonsToLbs(Number(totalTons)) : undefined,
        deliveryDate,
        farmName,
        groupName,
        binsData
      };
    }

    return {
      totalPounds: undefined,
      deliveryDate: 'TBD',
      farmName: farm?.name || '',
      groupName: pig_group?.name || '',
      binsData: []
    };
  }, [
    additional_data,
    message,
    total_tons,
    total_pounds,
    delivery_date,
    farm_name,
    group_name,
    bins_data,
    farm,
    pig_group
  ]);

  return (
    <ActivityBox
      className={className}
      isFlagged={flagged}
      isTruncated={isTruncated}
      hasMarker={!isChildActivity}
      isUnseen={!is_viewed}
      markAsSeen={markAsSeen}
      onViewFullActivity={onViewFullActivity}
    >
      {!isMinimized &&
        <LastCommentInfo activity={activity} currentUser={currentUser} />
      }
      <ActivityHeader
        activityId={id}
        onOpenDropdown={(e) => openDropdown(e, defaultDropdownOptions)}
        onDoubleClick={toggleMinimized}
      >
        <ActivityAuthorLine
          activity={activity}
          currentUser={currentUser}
          minimized={isMinimized}
        >
          <FormattedMessage
            tagName="div"
            id="component.feedOrderActivity.submitted"
            defaultMessage="{name} submitted a Feed Order"
            values={{
              name: user?.full_name || intl.formatMessage({ id: 'component.feedOrderActivity.unknownUser' }),
              b: (msg) => <b>{msg}</b>,
            }}
          />
        </ActivityAuthorLine>
      </ActivityHeader>

      <ActivityBodyCollapse isClosed={isMinimized}>
        <div className={cn('feed-order-content')}>
          <div className={cn('timestamp')}>
            {moment(created_at).format('MMM DD, YYYY, hh:mm a')}
          </div>

          <ActivityMentions isAdmin={isAdmin} users={mentions} />
        </div>

        <ActivityCheckupBox isAdmin={isAdmin} activity={activity} className={cn({ 'mb-15': isChildActivity })}>
          {pig_group && (
            <div className={cn('collapses-container')}>
              <CollapsesBox className={cn('group-collapse-box')}>
                <Collapse
                  titleClassName={cn('group-info-title')}
                  title={
                    (
                      <Link to={`/barnsheets/groups/${pig_group.id}`} className="color-primary">
                        {pig_group.name}
                      </Link>
                    )
                  }
                  isCollapsed={false}
                >
                  <div className={cn('feed-order-details')}>
                    <div className={cn('order-quantity-section')}>
                      <div className={cn('detail-row')}>
                        <span className={cn('detail-label')}>
                          <FormattedMessage id="component.feedOrderActivity.orderQuantity" />
                        </span>
                        <span className={cn('detail-value', 'total-quantity')}>
                          {typeof parsedData.totalPounds === 'number' ? (
                            <>
                              {formatNumber(
                                currentUser?.current_company?.measurement_system === 'metric'
                                  ? convertLbsToKg(parsedData.totalPounds)
                                  : Math.round(parsedData.totalPounds)
                              )}&nbsp;
                              <FormattedMessage
                                id={
                                  currentUser?.current_company?.measurement_system === 'metric'
                                    ? 'general.weight.kg'
                                    : 'general.weight.lbs'
                                }
                              />
                            </>
                          ) : (
                            'N/A'
                          )}
                        </span>
                      </div>
                    </div>

                    {parsedData.binsData.length > 0 && (
                      <div className={cn('bins-section')}>
                        {parsedData.binsData.map((bin, index) => (
                          <div key={index} className={cn('detail-row', 'bin-row')}>
                            <span className={cn('detail-label', 'bin-label')}>{bin.name}</span>
                            <span className={cn('detail-value', 'bin-quantity')}>
                              {typeof bin.quantityPounds === 'number' ? (
                                <>
                                  {formatNumber(
                                    currentUser?.current_company?.measurement_system === 'metric'
                                      ? convertLbsToKg(bin.quantityPounds)
                                      : Math.round(bin.quantityPounds)
                                  )}&nbsp;
                                  <FormattedMessage
                                    id={
                                      currentUser?.current_company?.measurement_system === 'metric'
                                        ? 'general.weight.kg'
                                        : 'general.weight.lbs'
                                    }
                                  />
                                </>
                              ) : (
                                bin.quantityPounds || 'N/A'
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className={cn('delivery-section')}>
                      <div className={cn('detail-row')}>
                        <span className={cn('detail-label')}>
                          <FormattedMessage id="component.feedOrderActivity.deliveryDate" />
                        </span>
                        <span className={cn('detail-value')}>
                          {formatDateForUser(parsedData.deliveryDate, countryCode)}
                        </span>
                      </div>
                    </div>
                  </div>
                </Collapse>
              </CollapsesBox>
            </div>
          )}
        </ActivityCheckupBox>

        {!isChildActivity && (
          <>
            <ActivityActionsBar
              activity={activity}
              currentUser={currentUser}
              openDropdown={openDropdown}
              setActivityFlag={setActivityFlag}
            />
            <FarmFeedRowComments activity={activity} />
            <FarmFeedRowAddComment activity={activity} />
          </>
        )}
      </ActivityBodyCollapse>
    </ActivityBox>
  );
};

FeedOrderActivity.contextTypes = {
  router: T.object.isRequired
};

FeedOrderActivity.propTypes = {
  className: T.string,
  activity: T.object.isRequired,
  currentUser: T.object.isRequired,
  markAsSeen: T.func,
  isChildActivity: T.bool,
  openDropdown: T.func.isRequired,
  setActivityFlag: T.func.isRequired,
  // from hoc
  isTruncated: T.bool.isRequired,
  isMinimized: T.bool.isRequired,
  toggleMinimized: T.func.isRequired,
  onViewFullActivity: T.func.isRequired,
  defaultDropdownOptions: T.array.isRequired,
  countryCode: T.string,
};

FeedOrderActivity.defaultProps = {
  isChildActivity: false,
};

const ConnectedComponent = connect(
  (state) => ({
    countryCode: getCurrentCountryCode(state),
  })
)(FeedOrderActivity);

export default withActivityController(ConnectedComponent, { affectRerenderKeys });
